/**
 * SubscriptionSection Component
 * Subscription status, tier display, billing details, and upgrade buttons
 */

import { DowngradeWarningModal } from '@components/ui/DowngradeWarningModal';
import type { User } from '@contexts/AuthContext';
import { useSubscriptionStatus } from '@hooks/useSubscriptionStatus';
import { TIER_PRICING } from '@parlay-picker/shared/subscriptionTier';
import { cn } from '@utils/cn';
import { type ReactElement, useCallback, useState } from 'react';

interface SubscriptionSectionProps {
  // User from top-level auth (already available in parent)
  user: User | null;

  // Tier flags (already computed at top level via useFeatureAccess)
  isPremium: boolean;
  isPro: boolean;
  isElite: boolean;
  isFree: boolean;

  // Handlers
  onUpgradeClick: () => void;
}

export function SubscriptionSection({
  user,
  isPremium,
  isPro,
  isElite,
  isFree,
  onUpgradeClick,
}: SubscriptionSectionProps): ReactElement {
  const { subscription, loading, openBillingPortal, portalLoading } = useSubscriptionStatus();
  const [showDowngradeWarning, setShowDowngradeWarning] = useState(false);

  const isPaidUser = (isPro || isPremium || isElite) && user?.role !== 'admin';

  const handleManageBillingClick = useCallback((): void => {
    if (isPaidUser) {
      setShowDowngradeWarning(true);
    } else {
      void openBillingPortal();
    }
  }, [isPaidUser, openBillingPortal]);

  return (
    <section data-testid="settings-subscription-section" className="mb-6">
      <h3 className="text-lg font-medium text-theme-text mb-2">Subscription</h3>
      <div className="bg-theme-surface rounded-lg p-4 border border-theme-border">
        {loading ? (
          <div className="flex items-center gap-2 text-theme-text-secondary">
            <div className="w-4 h-4 border-2 border-theme-border border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading subscription...</span>
          </div>
        ) : (
          <>
            {/* Plan + Status Row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {user?.role === 'admin' && (
                  <span className="px-2.5 py-1 rounded text-xs font-semibold bg-amber-500/15 text-amber-400">
                    Admin
                  </span>
                )}
                <span
                  className={cn(
                    'px-2.5 py-1 rounded text-xs font-semibold',
                    isElite && user?.role !== 'admin'
                      ? 'bg-indigo-500/15 text-indigo-400'
                      : isPremium && user?.role !== 'admin'
                        ? 'bg-purple-500/15 text-purple-300'
                        : isPro && user?.role !== 'admin'
                          ? 'bg-theme-primary/15 text-theme-primary'
                          : 'bg-theme-surface text-theme-text-secondary',
                  )}
                >
                  {user?.role === 'admin'
                    ? 'Free'
                    : isElite
                      ? 'Elite'
                      : isPremium
                        ? 'Premium'
                        : isPro
                          ? 'Pro'
                          : 'Free'}
                </span>
                {isPro && !isPremium && !isElite && user?.role !== 'admin' && (
                  <span className="text-xs text-theme-text-tertiary">
                    {TIER_PRICING.pro.price}/mo
                  </span>
                )}
                {isPremium && !isElite && user?.role !== 'admin' && (
                  <span className="text-xs text-theme-text-tertiary">
                    {TIER_PRICING.premium.price}/mo
                  </span>
                )}
                {isElite && user?.role !== 'admin' && (
                  <span className="text-xs text-theme-text-tertiary">
                    {TIER_PRICING.elite.price}/mo
                  </span>
                )}
              </div>
              {subscription && (
                <span
                  className={cn(
                    'px-2 py-0.5 rounded text-xs font-medium',
                    subscription.status === 'active'
                      ? 'bg-theme-success/15 text-theme-success'
                      : subscription.status === 'past_due'
                        ? 'bg-theme-warning/15 text-theme-warning'
                        : subscription.status === 'canceled'
                          ? 'bg-theme-danger/15 text-theme-danger'
                          : 'bg-theme-surface text-theme-text-secondary',
                  )}
                >
                  {subscription.status === 'active'
                    ? 'Active'
                    : subscription.status === 'past_due'
                      ? 'Past Due'
                      : subscription.status === 'canceled'
                        ? 'Canceled'
                        : subscription.status.charAt(0).toUpperCase() +
                          subscription.status.slice(1)}
                </span>
              )}
            </div>

            {/* Billing Details */}
            {subscription?.currentPeriodEnd && subscription.status === 'active' && (
              <div className="text-sm text-theme-text-secondary mb-3">
                {subscription.cancelAtPeriodEnd ? (
                  <div
                    className={cn(
                      'p-2.5 rounded-lg border',
                      'bg-theme-warning/10 border-theme-warning/30',
                    )}
                  >
                    <p className={cn('text-sm', 'text-theme-warning')}>
                      Your subscription will cancel on{' '}
                      <span className="font-medium">
                        {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                      . You&apos;ll retain access until then.
                    </p>
                  </div>
                ) : (
                  <p>
                    Next billing date:{' '}
                    <span className="text-theme-text">
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </p>
                )}
              </div>
            )}

            {/* Past Due Warning */}
            {subscription?.status === 'past_due' && (
              <div
                className={cn(
                  'p-2.5 rounded-lg mb-3 border',
                  'bg-theme-warning/10 border-theme-warning/30',
                )}
              >
                <p className={cn('text-sm', 'text-theme-warning')}>
                  Your payment is past due. Please update your payment method to avoid losing
                  access.
                </p>
              </div>
            )}

            {/* Downgrade Warning */}
            {(isPro || isPremium || isElite) &&
              user?.role !== 'admin' &&
              !subscription?.cancelAtPeriodEnd && (
                <div
                  className={cn(
                    'p-2.5 rounded-lg mb-3 border',
                    'bg-theme-surface-hover border-theme-border',
                  )}
                >
                  <p className={cn('text-xs font-medium mb-1', 'text-theme-text-secondary')}>
                    If you cancel or downgrade:
                  </p>
                  <ul className={cn('text-xs space-y-0.5 ml-4', 'text-theme-text-tertiary')}>
                    {isElite && (
                      <>
                        <li>• Sharp money signals & +EV badges</li>
                        <li>• Middles & sharp signal alerts</li>
                        <li>• Step-by-step prediction math</li>
                      </>
                    )}
                    {(isPremium || isElite) && (
                      <>
                        <li>• Custom themes (resets to default)</li>
                        <li>• Detailed AI analysis mode (resets to standard)</li>
                      </>
                    )}
                    {(isPro || isPremium || isElite) && (
                      <>
                        <li>• Follower permissions (all disabled)</li>
                        <li>• Social sharing features</li>
                        <li>• Best line finder</li>
                        <li>• Daily quota adjusts to new tier limits</li>
                      </>
                    )}
                  </ul>
                  <p className={cn('text-xs mt-1.5', 'text-theme-text-tertiary')}>
                    Changes take effect at the end of your current billing period. You&apos;ll keep
                    your data and performance history.
                  </p>
                </div>
              )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 mt-1">
              {(isFree || user?.role === 'admin') && (
                <button
                  type="button"
                  onClick={onUpgradeClick}
                  className="px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill text-sm font-medium rounded-lg transition-colors"
                >
                  {user?.role === 'admin' ? 'Change Plan' : 'Upgrade'}
                </button>
              )}
              {isPro && !isPremium && !isElite && (
                <button
                  type="button"
                  onClick={onUpgradeClick}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Upgrade to Premium
                </button>
              )}
              {isPremium && !isElite && (
                <button
                  type="button"
                  onClick={onUpgradeClick}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Upgrade to Elite
                </button>
              )}
              {subscription && subscription.status !== 'canceled' && user?.role !== 'admin' && (
                <button
                  type="button"
                  onClick={handleManageBillingClick}
                  disabled={portalLoading}
                  className="px-4 py-2 bg-theme-bg hover:bg-theme-border disabled:bg-theme-surface disabled:text-theme-text-tertiary text-theme-text text-sm font-medium rounded-lg transition-colors"
                >
                  {portalLoading ? 'Opening...' : 'Manage Billing'}
                </button>
              )}
              {subscription?.status === 'canceled' && user?.role !== 'admin' && (
                <button
                  type="button"
                  onClick={onUpgradeClick}
                  className="px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill text-sm font-medium rounded-lg transition-colors"
                >
                  Resubscribe
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {showDowngradeWarning && (
        <DowngradeWarningModal
          isPremium={isPremium}
          isPro={isPro}
          isElite={isElite}
          portalLoading={portalLoading}
          onConfirm={() => {
            setShowDowngradeWarning(false);
            void openBillingPortal();
          }}
          onDismiss={() => setShowDowngradeWarning(false)}
        />
      )}
    </section>
  );
}
